import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { QueryGuard } from '@/components/query-guard'
import { DataRoomContent } from '@/features/pitch'
import { AnalyticsDashboard } from '@/features/startup'
import {
  useAdminDataRoomAnalyticsQuery,
  useAdminDataRoomByIdQuery,
  useGetDataRoomDocumentDownloadMutation,
  useGetDataRoomVideoUrlMutation,
  useGetPitchDeckDownloadMutation,
  useStartupConfigQuery,
} from '@/hooks/api'

export const Route = createFileRoute('/_protected/admin/datarooms/$id')({
  component: AdminDataRoomDetailPage,
})

type Tab = 'overview' | 'analytics'

function AdminDataRoomDetailPage() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  const dataRoomQuery = useAdminDataRoomByIdQuery(id)
  const analyticsQuery = useAdminDataRoomAnalyticsQuery(id)
  const { data: configResponse } = useStartupConfigQuery()

  const { mutateAsync: fetchDataRoomDocumentUrl } = useGetDataRoomDocumentDownloadMutation()
  const { mutateAsync: fetchPitchDeckUrl } = useGetPitchDeckDownloadMutation()
  const { mutateAsync: getVideoUrl } = useGetDataRoomVideoUrlMutation()

  const productStages = configResponse?.data?.productStages ?? []

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
  ]

  return (
    <QueryGuard query={dataRoomQuery} errorMessage="Failed to load data room">
      {(data) => {
        const { dataRoom, content } = data
        const token = dataRoom.token

        return (
          <>
            <nav className="border-b border-border">
              <div className="flex flex-wrap items-center gap-2 py-3">
                <Link
                  to="/admin/datarooms"
                  className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
                >
                  <ArrowLeft className="size-4" />
                  Data Rooms
                </Link>
                <ChevronRight className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-medium">{content.startup.name}</h2>
                <span className="font-mono text-xs text-muted-foreground">{token}</span>
                <Badge variant={dataRoom.revokedAt ? 'destructive' : 'success'} size="sm">
                  {dataRoom.revokedAt ? 'Revoked' : 'Active'}
                </Badge>
              </div>
              <div className="-mb-px flex gap-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`border-b-2 px-3 py-2 text-sm font-medium ${
                      activeTab === tab.id
                        ? 'border-[#f97316] text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
            <div className="py-6">
              {activeTab === 'overview' && (
                <DataRoomContent
                  data={content}
                  productStages={productStages}
                  fetchVideoUrl={
                    content.pitchSession?.videoKey
                      ? async () => {
                          const res = await getVideoUrl({ path: { token } })
                          if (res.data?.url) return res.data.url
                          throw new Error('Failed to get video URL')
                        }
                      : undefined
                  }
                  fetchPitchDeckUrl={async () => {
                    const res = await fetchPitchDeckUrl({ path: { token } })
                    if (res.data?.downloadUrl) return res.data.downloadUrl
                    throw new Error('Failed to get download URL')
                  }}
                  fetchDocumentUrl={async (documentId) => {
                    const res = await fetchDataRoomDocumentUrl({
                      path: { token, documentId },
                    })
                    if (res.data?.downloadUrl) return res.data.downloadUrl
                    throw new Error('Failed to get download URL')
                  }}
                />
              )}
              {activeTab === 'analytics' && (
                <div className="space-y-6">
                  {analyticsQuery.data?.data ? (
                    <AnalyticsDashboard analytics={analyticsQuery.data.data} />
                  ) : analyticsQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading analytics...</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No analytics data available.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )
      }}
    </QueryGuard>
  )
}
